export function hasValue(value) {
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some((item) => String(item || '').trim());
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function parentCondition(when) {
  if (!when || typeof when !== 'object') return when;
  const { any_of: _anyOf, ...parent } = when;
  return parent;
}

function checkboxSelected(value) {
  if (value === true) return true;
  return ['yes', 'true', 'on', '1'].includes(String(value ?? '').trim().toLowerCase());
}

export function conditionMatches(when, values) {
  if (!when) return true;
  const parent = parentCondition(when);
  if (parent.any_equals !== undefined && parent.any_equals !== null && String(parent.any_equals).trim() !== '') {
    const expected = String(parent.any_equals).trim().toLowerCase();
    return (parent.fields || []).some((field) => conditionMatches({ field, equals: expected }, values));
  }
  if (Object.prototype.hasOwnProperty.call(parent, 'present')) {
    const actual = String(values?.[parent.field] ?? '').trim();
    return parent.present ? Boolean(actual) : !actual;
  }
  const actual = String(values?.[parent.field] ?? '').trim().toLowerCase();
  if (parent.not_equals !== undefined && parent.not_equals !== null && String(parent.not_equals).trim() !== '') {
    const expected = String(parent.not_equals).trim().toLowerCase();
    return Boolean(actual) && actual !== expected;
  }
  const expected = String(parent.equals ?? '').trim().toLowerCase();
  if (['yes', 'true', 'on', '1'].includes(expected)) {
    return ['yes', 'true', 'on', '1'].includes(actual);
  }
  return actual === expected;
}

export function fieldVisible(field, step, values) {
  return conditionMatches(step?.show_when?.[field], values);
}

export function fieldRequired(field, step, values, optionalFields = []) {
  if (field.startsWith('parent_') || field === 'other_terms') return false;
  if (!fieldVisible(field, step, values)) return false;
  const requireWhen = step?.require_when?.[field];
  if (requireWhen) {
    if (!conditionMatches(requireWhen, values)) return false;
    if (Array.isArray(requireWhen.any_of)) {
      return !requireWhen.any_of.some((name) => checkboxSelected(values?.[name]));
    }
    return true;
  }
  if (step?.optional || optionalFields.includes(field)) return false;
  return true;
}

export function optionsForField(field, step) {
  if (step?.field_options?.[field]?.length) return step.field_options[field];
  if (!step?.options?.length) return [];
  if ((step.fields || []).length === 1 && step.fields[0] === field) return step.options;
  if (inputForField(field, step) === 'radio') return step.options;
  return [];
}

export function stripPrintedItemNumber(label) {
  return String(label || '').replace(/^\d+\.\s*/, '');
}

export function labelForField(field, step, fallbacks = {}) {
  return stripPrintedItemNumber(step?.field_labels?.[field] || fallbacks[field] || field);
}

export function placeholderForField(field, step, values, optionalFields = []) {
  const explicit = step?.field_placeholders?.[field];
  if (explicit) return explicit;
  const kind = inputForField(field, step);
  if (!['text', 'textarea', 'number'].includes(kind)) return '';
  if (fieldRequired(field, step, values || {}, optionalFields)) return '';
  return 'Optional';
}

export function inputForField(field, step) {
  if (step?.field_inputs?.[field]) return step.field_inputs[field];
  if (step?.input && (step.fields || []).length === 1) return step.input;
  if (step?.input === 'radio' && field === (step.fields || [])[0]) return 'radio';
  if (step?.input === 'checkbox' && field === (step.fields || [])[0]) return 'checkbox';
  if (field === 'addendum_body' || field === 'other_terms') return 'textarea';
  if (field.includes('price') || field.includes('increment') || field.includes('percent') || field.includes('days')) {
    return 'number';
  }
  if (field.includes('deadline')) return 'datetime';
  if (field.includes('date')) return 'date';
  return 'text';
}

export function inputTypeForField(field, step) {
  const kind = inputForField(field, step);
  if (kind === 'number') return 'number';
  if (kind === 'datetime' || field === 'acceptance_deadline') return 'datetime-local';
  if (kind === 'date') return 'date';
  return 'text';
}

function optionValuesForField(field, step) {
  const opts = optionsForField(field, step);
  if (!opts.length) return null;
  const values = new Set(
    opts
      .map((opt) => (
        opt && typeof opt === 'object' ? String(opt.value ?? '').trim() : String(opt ?? '').trim()
      ))
      .filter(Boolean),
  );
  return values.size ? values : null;
}

export function dropHiddenFields(steps, values) {
  const next = { ...(values || {}) };
  let changed = true;
  while (changed) {
    changed = false;
    const shown = new Set();
    const mentioned = new Set();
    const allowedOptions = new Map();
    (steps || []).forEach((step) => {
      (step.fields || []).forEach((field) => {
        mentioned.add(field);
        if (!fieldVisible(field, step, next)) return;
        shown.add(field);
        const allowed = optionValuesForField(field, step);
        if (!allowed) return;
        const existing = allowedOptions.get(field) || new Set();
        allowed.forEach((value) => existing.add(value));
        allowedOptions.set(field, existing);
      });
    });
    mentioned.forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(next, field)) return;
      const value = next[field];
      const allowed = allowedOptions.get(field);
      const optionMismatch = Boolean(
        shown.has(field)
        && allowed
        && hasValue(value)
        && !allowed.has(String(value).trim()),
      );
      if (shown.has(field) && !optionMismatch) return;
      delete next[field];
      if (hasValue(value) || value === false) {
        changed = true;
      }
    });
  }
  return next;
}

export function visibleFieldsForStep(step, values) {
  return (step?.fields || []).filter((field) => fieldVisible(field, step, values));
}

export const PROPERTY_ADDRESS_FIELD_NAMES = new Set([
  'property_address',
  'property_address_city',
  'property_address_state',
  'property_address_zip_code',
  'street_address',
  'city',
  'state',
  'zip_code',
  'q_p1_280_text',
  'q_p1_291_text',
]);

const APPENDIX_A_LOCATION_FIELDS = new Set(['q_p1_280_text', 'q_p1_291_text']);

export function isPropertyAddressStep(step) {
  const fields = (step?.fields || []).filter((field) => !field.startsWith('parent_'));
  return Boolean(fields.length) && fields.every((field) => PROPERTY_ADDRESS_FIELD_NAMES.has(field));
}

export function hasCompletePropertyAddress(values) {
  const data = values || {};
  if (hasValue(data.q_p1_280_text) && hasValue(data.q_p1_291_text)) return true;
  return Boolean(
    hasValue(data.property_address || data.street_address)
    && hasValue(data.property_address_city || data.city)
    && hasValue(data.property_address_state || data.state)
    && hasValue(data.property_address_zip_code || data.zip_code),
  );
}

export function stepIsLocationSkip(step, values) {
  if (!isPropertyAddressStep(step)) return false;
  const fields = (step.fields || []).filter((field) => !field.startsWith('parent_'));
  const locationFields = fields.filter((field) => APPENDIX_A_LOCATION_FIELDS.has(field));
  if (locationFields.length && locationFields.every((field) => hasValue(values?.[field]))) {
    return true;
  }
  return hasCompletePropertyAddress(values);
}

export function normalizeRepresentedSides(value) {
  return (Array.isArray(value) ? value : [])
    .map((side) => String(side || '').trim().toLowerCase())
    .filter((side) => side === 'buyer' || side === 'seller');
}

export function inferCancellingSide(values) {
  if (hasValue(values?.cancelling_side)) return values.cancelling_side;
  const sides = normalizeRepresentedSides(values?.represented_sides);
  return sides.length === 1 ? sides[0] : '';
}

function dualAgency(values) {
  const sides = normalizeRepresentedSides(values?.represented_sides);
  return sides.includes('buyer') && sides.includes('seller');
}

function conditionDependsOnUnansweredField(when, values) {
  if (!when || typeof when !== 'object') return false;
  const parent = parentCondition(when);
  if (parent.any_equals !== undefined && parent.any_equals !== null && String(parent.any_equals).trim() !== '') {
    return (parent.fields || []).every((field) => !hasValue(values?.[field]));
  }
  if (!parent.field) return false;
  return !hasValue(values?.[parent.field]);
}

function stepMightBecomeVisible(step, values) {
  if (!step || stepIsVisible(step, values)) return false;
  return (step.fields || []).some((field) => (
    conditionDependsOnUnansweredField(step?.show_when?.[field], values)
  ));
}

function pendingFollowUpKey(step) {
  const keys = Object.values(step?.show_when || {}).map((when) => {
    const parent = parentCondition(when);
    if (parent.any_equals !== undefined) {
      return `any:${(parent.fields || []).slice().sort().join(',')}`;
    }
    return String(parent.field || step.step_id);
  });
  return keys.sort().join('|') || step.step_id;
}

export function stepIsVisible(step, values) {
  if (!step) return false;
  if (step.step_id === 'parent' || step.step_id === 'principal_emails') return true;
  if (step.step_id === 'cancelling_side' && hasValue(values?.cancelling_side) && !dualAgency(values)) {
    return false;
  }
  if (stepIsLocationSkip(step, values)) return false;
  return visibleFieldsForStep(step, values).length > 0;
}

export function progressStepIndexes(steps, values) {
  const included = [];
  const pendingKeys = new Set();
  (steps || []).forEach((step, index) => {
    if (stepIsVisible(step, values)) {
      included.push(index);
      return;
    }
    if (stepMightBecomeVisible(step, values)) {
      const key = pendingFollowUpKey(step);
      if (!pendingKeys.has(key)) {
        pendingKeys.add(key);
        included.push(index);
      }
    }
  });
  return included;
}

export function nextVisibleStepIndex(steps, fromIndex, values, direction = 1) {
  const list = steps || [];
  let index = fromIndex + direction;
  while (index >= 0 && index < list.length) {
    if (stepIsVisible(list[index], values)) return index;
    index += direction;
  }
  return direction > 0 ? list.length : -1;
}

export function visibleStepIndexes(steps, values) {
  return (steps || []).map((_, index) => index).filter((index) => stepIsVisible(steps[index], values));
}

export function firstIncompleteStepIndex(steps, values, optionalFields = []) {
  if (!steps || !steps.length) return 0;
  const data = values || {};
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    if (!stepIsVisible(step, data)) continue;
    const visible = visibleFieldsForStep(step, data);
    const missing = visible.some((field) => !hasValue(data[field]));
    if (missing) return i;
  }
  return Math.max(steps.length - 1, 0);
}

export function firstIncompleteRequiredStepIndex(steps, values, optionalFields = [], emailGaps = []) {
  if (!steps || !steps.length) return -1;
  const data = values || {};
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    if (step?.step_id === 'principal_emails') {
      if (emailGaps.length) return i;
      continue;
    }
    if (!stepIsVisible(step, data)) continue;
    const missing = visibleFieldsForStep(step, data).some((field) => (
      fieldRequired(field, step, data, optionalFields) && !hasValue(data[field])
    ));
    if (missing) return i;
  }
  return -1;
}
